import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Search, Phone, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable "Contacto Padres" section: search a student and show the parent's cell
// number (from the imported file). Used by Control Escolar, Orientador and the
// teacher's Grupo Tutorado. Native input for a fully fluid search (no lag).
const ContactoPadresSection = ({ showHeader = true }) => {
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const handleSearch = async () => {
    const q = term.trim();
    if (!q) { toast.error('Escribe el nombre o matrícula del alumno'); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/contacto/search-students`, { ...authHeaders(), params: { name: q } });
      setResults(res.data.students || []);
      if (!res.data.found) toast.info('No se encontraron alumnos');
    } catch (e) {
      toast.error('Error al buscar');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="contacto-padres-page">
      {showHeader && (
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Contacto Padres</h1>
            <p className="text-sm text-slate-500">Consulta el número de celular del padre/madre/tutor del alumno</p>
          </div>
        </div>
      )}

      <Card className="p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o matrícula..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent pl-10 pr-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
              data-testid="contacto-search-input"
            />
          </div>
          <Button onClick={handleSearch} disabled={searching} data-testid="contacto-search-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>

        {results && results.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="contacto-results">
            {results.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200" data-testid={`contacto-student-${i}`}>
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-800">{s.name}</p>
                    <p className="text-xs text-slate-500">
                      {s.matricula ? `Matrícula: ${s.matricula}` : 'Sin matrícula'}{s.grupo ? ` · Grupo: ${s.grupo}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-right">
                  <Phone className="w-4 h-4 text-indigo-500" />
                  {s.celular ? (
                    <span className="font-bold text-slate-800" data-testid={`contacto-cel-${i}`}>{s.celular}</span>
                  ) : (
                    <span className="text-sm text-slate-400 italic" data-testid={`contacto-cel-${i}`}>Dato no disponible</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {results && results.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">Sin resultados.</p>
        )}
      </Card>
    </div>
  );
};

export default ContactoPadresSection;
